import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type BillingInfo } from '../lib/api';
import s from './BillingPage.module.css';

/* -------------------------------------------------------------------------- */
/*  Plan data                                                                  */
/* -------------------------------------------------------------------------- */

const PLANS = [
  {
    name: 'Free',
    price: '$0/mo',
    domains: '3',
    reportsPerWeek: '100',
    retention: '7 days',
    users: '1',
    dnsMonitoring: 'Basic',
    support: 'Community',
  },
  {
    name: 'Pro',
    price: '$19/mo',
    domains: '25',
    reportsPerWeek: '5,000',
    retention: '90 days',
    users: '5',
    dnsMonitoring: 'Advanced',
    support: 'Email',
  },
  {
    name: 'Business',
    price: '$79/mo',
    domains: 'Unlimited',
    reportsPerWeek: '50,000',
    retention: '1 year',
    users: 'Unlimited',
    dnsMonitoring: 'Advanced + Alerts',
    support: 'Priority',
  },
];

const PLAN_ROWS = [
  { key: 'domains', label: 'Domains' },
  { key: 'reportsPerWeek', label: 'Reports / week' },
  { key: 'retention', label: 'Data retention' },
  { key: 'users', label: 'Team members' },
  { key: 'dnsMonitoring', label: 'DNS monitoring' },
  { key: 'support', label: 'Support' },
] as const;

/* -------------------------------------------------------------------------- */
/*  BillingPage                                                                */
/* -------------------------------------------------------------------------- */

export default function BillingPage() {
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    api.settings
      .billing()
      .then((data) => {
        if (!cancelled) {
          setBilling(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load billing');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <div className={s.loading}>Loading billing&hellip;</div>;
  if (error) return <div className={s.error}>{error}</div>;

  // Fallback values
  const plan = billing?.plan ?? 'free';
  const usage = billing?.usage ?? { domains: 0, maxDomains: 3, reports: 0 };
  const reportsLimit = plan === 'free' ? 100 : plan === 'pro' ? 5000 : 50000;
  const usagePct = Math.min((usage.reports / reportsLimit) * 100, 100);
  const isOverLimit = usage.reports >= reportsLimit;
  const rejectedCount = Math.max(0, usage.reports - reportsLimit);

  const currentPlanName =
    plan === 'free' ? 'Free' : plan === 'pro' ? 'Pro' : 'Business';

  return (
    <>
      {/* Back link */}
      <Link to="/app/settings" className={s.backLink}>
        &larr; Settings
      </Link>

      {/* Header */}
      <div className={s.header}>
        <h1 className={s.title}>Billing</h1>
      </div>

      {/* Over limit warning */}
      {isOverLimit && (
        <div className={s.warningBanner}>
          Reports are being rejected. Upgrade to continue receiving DMARC
          reports for your domains.
        </div>
      )}

      {/* Current plan + Usage cards */}
      <div className={s.cardsRow}>
        {/* Current plan */}
        <div className={s.card}>
          <div className={s.cardTitle}>Current Plan</div>
          <div className={s.planName}>{currentPlanName}</div>
          <div className={s.planPrice}>
            {PLANS.find((p) => p.name === currentPlanName)?.price ?? '$0/mo'}
          </div>

          <div className={s.planDetail}>
            <span className={s.planDetailLabel}>Domains</span>
            <span>
              {usage.domains} / {usage.maxDomains}
            </span>
          </div>
          <div className={s.planDetail}>
            <span className={s.planDetailLabel}>Reports / week</span>
            <span>{reportsLimit.toLocaleString()}</span>
          </div>
        </div>

        {/* Usage this week */}
        <div className={s.card}>
          <div className={s.cardTitle}>Usage This Week</div>

          <div className={s.usageHeader}>
            <span className={s.usageValue}>{usage.reports.toLocaleString()}</span>
            <span className={s.usageLabel}>
              of {reportsLimit.toLocaleString()} reports
            </span>
          </div>

          <div className={s.usageBarTrack}>
            <div
              className={isOverLimit ? s.usageBarFillDanger : s.usageBarFill}
              style={{ width: `${usagePct}%` }}
            />
          </div>

          <div className={s.usageDetail}>
            Reports processed: {Math.min(usage.reports, reportsLimit).toLocaleString()}
          </div>
          {rejectedCount > 0 && (
            <div className={`${s.usageDetail} ${s.usageDetailDanger}`}>
              Reports rejected: {rejectedCount.toLocaleString()}
            </div>
          )}
        </div>
      </div>

      {/* Plan comparison */}
      <h2 className={s.sectionTitle}>Compare Plans</h2>

      <div className={s.planTableWrap}>
        <table className={s.planTable}>
          <thead>
            <tr>
              <th>Feature</th>
              {PLANS.map((p) => (
                <th
                  key={p.name}
                  className={p.name === currentPlanName ? s.currentPlanCol : undefined}
                >
                  <span
                    className={
                      p.name === currentPlanName
                        ? s.planColHeaderCurrent
                        : s.planColHeader
                    }
                  >
                    {p.name}
                  </span>
                  {p.name === currentPlanName && (
                    <span className={s.currentTag}>Current</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ fontWeight: 500, color: 'var(--text-tertiary)' }}>
                Price
              </td>
              {PLANS.map((p) => (
                <td
                  key={p.name}
                  className={p.name === currentPlanName ? s.currentPlanCol : undefined}
                  style={{ fontWeight: 600 }}
                >
                  {p.price}
                </td>
              ))}
            </tr>
            {PLAN_ROWS.map((row) => (
              <tr key={row.key}>
                <td style={{ fontWeight: 500, color: 'var(--text-tertiary)' }}>
                  {row.label}
                </td>
                {PLANS.map((p) => (
                  <td
                    key={p.name}
                    className={p.name === currentPlanName ? s.currentPlanCol : undefined}
                  >
                    {p[row.key]}
                  </td>
                ))}
              </tr>
            ))}
            {/* Upgrade buttons row */}
            <tr>
              <td />
              {PLANS.map((p) => {
                const isCurrent = p.name === currentPlanName;
                const isDowngrade =
                  PLANS.findIndex((x) => x.name === p.name) <
                  PLANS.findIndex((x) => x.name === currentPlanName);

                return (
                  <td
                    key={p.name}
                    className={isCurrent ? s.currentPlanCol : undefined}
                  >
                    {isCurrent ? (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                        Current plan
                      </span>
                    ) : isDowngrade ? null : (
                      <button type="button" className={s.btnAccent} disabled>
                        Upgrade
                      </button>
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}
